import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, LoadingState, PageHeader, Tabs } from '../../components/ui/Primitives';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { formatDate, toDate } from '../../utils/display';

const pad = value => String(value).padStart(2, '0');
const keyFor = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const relevantToStudent = (item, user) => {
  if (item.courseId && user?.courseId) return item.courseId === user.courseId;
  if (item.intake && user?.intake) return item.intake === user.intake;
  return !item.courseId && !item.intake;
};

export default function StudentCalendar() {
  const { user } = useAuth();
  const { calendarEvents = [], assignments = [], loading = false } = useData() || {};
  const today = useMemo(() => { const value = new Date(); value.setHours(0, 0, 0, 0); return value; }, []);
  const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [view, setView] = useState('month');
  const items = useMemo(() => {
    const assignmentItems = assignments.filter(item => relevantToStudent(item, user)).map(item => {
      const date = toDate(item.dueDate);
      return date ? { id: `assignment-${item.id}`, recordId: item.id, title: item.title || 'Assignment', date, kind: 'Assignment', source: 'assignment', detail: item.module || '' } : null;
    }).filter(Boolean);
    const eventItems = calendarEvents.filter(item => relevantToStudent(item, user)).map(item => {
      const date = toDate(item.start || item.startDate || item.date || item.eventDate);
      return date ? { id: `event-${item.id}`, title: item.title || item.name || 'Academic event', date, kind: item.type || item.category || 'Event', source: 'event', detail: item.location || '' } : null;
    }).filter(Boolean);
    return [...assignmentItems, ...eventItems].sort((a, b) => a.date - b.date);
  }, [assignments, calendarEvents, user]);
  const upcoming = items.filter(item => item.date >= today);
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1).getDay();
  const offset = firstDay === 0 ? 6 : firstDay - 1;
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = Array.from({ length: offset }, (_, index) => ({ empty: true, id: `empty-${index}` }));
  for (let day = 1; day <= days; day += 1) {
    const date = new Date(month.getFullYear(), month.getMonth(), day);
    cells.push({ id: keyFor(date), date, items: items.filter(item => keyFor(item.date) === keyFor(date)) });
  }

  const agenda = list => list.length ? <div className="calendar-agenda">{list.map(item => (
    <div className="agenda-row" key={item.id}>
      <time>{formatDate(item.date, { weekday: 'short' })}</time>
      <strong>{item.title}</strong>
      <small>{item.kind}{item.detail ? ` · ${item.detail}` : ''}</small>
      {item.source === 'assignment' && <Link className="ui-text-action" to={`/student/assignments?assignment=${item.recordId}`}>View assignment →</Link>}
    </div>
  ))}</div> : <EmptyState title="Nothing scheduled" description="Published academic dates will appear here." />;

  return (
    <div>
      <PageHeader eyebrow="Schedule" title="Calendar" description="Academic deadlines and events published for your course." />
      <div className="calendar-toolbar"><Tabs items={[{ value: 'month', label: 'Month' }, { value: 'upcoming', label: 'Upcoming' }]} active={view} onChange={setView} ariaLabel="Calendar view" /></div>
      {loading ? <LoadingState label="Loading calendar…" /> : view === 'upcoming' ? agenda(upcoming) : (
        <div className="calendar-layout">
          <section className="calendar-panel" aria-label="Monthly calendar">
            <div className="calendar-month-nav"><button type="button" aria-label="Previous month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>‹</button><h2>{month.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}</h2><button type="button" aria-label="Next month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>›</button></div>
            <div className="calendar-grid">{['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(label => <div className="calendar-weekday" key={label}>{label}</div>)}{cells.map(cell => cell.empty ? <div className="calendar-day is-empty" key={cell.id} /> : <div className={`calendar-day ${keyFor(cell.date) === keyFor(today) ? 'is-today' : ''}`} key={cell.id}><span>{cell.date.getDate()}</span>{cell.items.slice(0, 2).map(item => <em className={item.source === 'event' ? 'is-event' : ''} key={item.id}>{item.title}</em>)}{cell.items.length > 2 && <em>+{cell.items.length - 2} more</em>}</div>)}</div>
          </section>
          <aside><h2 className="ui-side-heading">Upcoming</h2>{agenda(upcoming.slice(0, 6))}</aside>
        </div>
      )}
    </div>
  );
}
